/**
 * Structured item-effects data layer — the NEW, mechanism-based layer that
 * coexists with the legacy `EffectPrimitive`/`pendingEffects` bare-primitive
 * tags in `item-effects.ts`.
 *
 * Rationale (from 2026-06-24-item-effects-design.md §Decisions):
 *   The KTS `pendingEffects` tags are human guesses and ~30/85 are mistagged.
 *   KTS has no clean 10-primitive model — we port the *real* per-item mechanism.
 *   This table is a curated, KTS-sourced hand-authored registry with provenance
 *   comments (// KTS BehaviorData.xml:NNNN …). Values are KTS-faithful;
 *   sourcing is robust and reviewable.
 *
 * Design contract:
 *   - Runtime dispatchers drive off THIS table, NOT `pendingEffects`.
 *   - `pendingEffects` is kept for `isUsable()` only (backward compat).
 *   - Items already handled by the pets feature are EXCLUDED here:
 *     ObedienceRod, HuntingMachete, BearShamansShroud, BeastmastersVambrace,
 *     MasterRangersMaille.
 *   - Later tasks (2–N) populate the remaining rows; each task's section is
 *     marked with its task number.
 *
 * See also: `item-effects.ts` (legacy EffectPrimitive / EFFECT_PRIMITIVES).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** When the effect fires. */
export type EffectTrigger = "on-hit" | "on-equip" | "on-consume" | "active" | "on-cast";

/** What mechanism the effect implements.
 *
 * on-hit:    "dot" | "lifesteal" | "aura-heal"
 * on-equip:  "warmth-factor"
 * active:    "summon" | "dash" | "leap" | "stealth" | "cc"
 * on-cast:   "cast-proc"
 * cc is also valid as on-hit (e.g. MurlocDoomhammer stun on hit).
 */
export type EffectKind =
  | "dot"          // damage-over-time (poison, fire, …)
  | "lifesteal"    // heal attacker a % of damage dealt
  | "aura-heal"    // on-hit → heal lowest-HP nearby ally
  | "warmth-factor"// on-equip → register a warmth drain multiplier
  | "summon"       // active → spawn minion(s)
  | "dash"         // active → charge to target
  | "leap"         // active → leap to terrain feature
  | "stealth"      // active → enter stealth
  | "cc"           // on-hit or active → incapacitation state
  | "cast-proc";   // on-cast → proc side-effect

/** Structured item effect with trigger, kind, and mechanism-specific params.
 *
 * `params` is deliberately loose (`Record<string, …>`) at this layer; each
 * runtime handler casts to its own param interface. Later tasks tighten this
 * per-kind as handlers are implemented. */
export interface ItemEffect {
  readonly trigger: EffectTrigger;
  readonly kind: EffectKind;
  readonly params: Record<string, number | string | boolean>;
}

// ---------------------------------------------------------------------------
// ITEM_EFFECTS table
//
// Key = ItemId (must exist in ITEMS registry — enforced by qa-item-effects.ts).
// Value = ordered list of ItemEffects; most items have exactly one.
//
// Task 1 seeds ONE row to prove the data layer end-to-end. Remaining rows
// are populated in Tasks 2–N.
// ---------------------------------------------------------------------------
export const ITEM_EFFECTS: Readonly<Record<string, ItemEffect[]>> = {

  // -- Task 2: weapon DoT rows (on-hit proc bus) ----------------------------

  /**
   * NoxiousSpiderDagger — on-hit poison DoT.
   * KTS: BehaviorData.xml:4894 "NoxiousVenom" CBehaviorBuff:
   *   Period 0.5s, Duration 10s, buffId "NoxiousVenom", biological, 5 stacks.
   *   DamageEffect "NoxiousVenomDamage": amount = alchemy/8 per tick (MapScript:57365).
   *   biological=true gate: only applies to biological targets.
   */
  NoxiousSpiderDagger: [
    {
      trigger: "on-hit",
      kind: "dot",
      params: {
        buffId: "NoxiousVenom",
        biological: true,  // KTS: BehaviorData.xml:4894 — biological-only
      },
    },
  ],

  /**
   * NoxiousSpiderDaggerMain — alt id for main-hand slot variant.
   * KTS MapScript:57373 — same NoxiousVenom behavior as NoxiousSpiderDagger.
   */
  NoxiousSpiderDaggerMain: [
    {
      trigger: "on-hit",
      kind: "dot",
      params: {
        buffId: "NoxiousVenom",
        biological: true, // KTS: MapScript:57373 — same as NoxiousSpiderDagger
      },
    },
  ],

  /**
   * VenomousArbalest — on-hit poison DoT.
   * KTS: BehaviorData.xml:9569, EffectData.xml:13260:
   *   Period 0.5s, Duration 5s, 2 dmg/tick, biological.
   */
  VenomousArbalest: [
    {
      trigger: "on-hit",
      kind: "dot",
      params: {
        buffId: "VenomousArbalestPoison",
        biological: true, // KTS: BehaviorData.xml:9569 — biological-only
      },
    },
  ],

  /**
   * BurningSavage — on-hit fire DoT.
   * KTS: BehaviorData.xml:5922, MapScript:7393:
   *   Period 0.5s, Duration 6.5s, 4 stacks, dmg/tick = 2·alchemy·stack.
   *   Not gated on biological.
   */
  BurningSavage: [
    {
      trigger: "on-hit",
      kind: "dot",
      params: {
        buffId: "UndyingFlames",
        // KTS: BehaviorData.xml:5922 — not biological-gated
      },
    },
  ],

  /**
   * FelFireTorch — on-hit fire DoT (Task 2) + on-equip warmth drain-reducer stub (Task 4).
   * KTS on-hit: BehaviorData.xml:7972, MapScript:60389:
   *   Period 0.25s, Duration 8s, 1 dmg/tick, biological, intensity gate.
   *   CONCERN: intensity tracking not yet implemented; gate defaults to 0
   *   (never fires). Stub hook in combat-procs.ts documents the gate.
   * KTS warmth: MS:4474 — 0%–80% drain reduction by kill-intensity.
   *   v1 has no kill-intensity → stub at ×1.0 (no reduction).
   *   See warmth-factors.ts for the full concern comment.
   */
  FelFireTorch: [
    {
      trigger: "on-hit",
      kind: "dot",
      params: {
        buffId: "FelFlames",
        biological: true,      // KTS: BehaviorData.xml:7972 — biological-only
        intensityGate: 35,     // KTS: MapScript:60389 — requires intensity >= 35
      },
    },
    {
      trigger: "on-equip",
      kind: "warmth-factor",
      params: {
        multiplier: 1.0,           // STUB — real: 1 - killIntensity/100 × 0.8
        behaviorId: "FelFireTorch",
      },
    },
  ],

  /**
   * MasterChefsRollingPin — on-hit melee-bash DoT.
   * KTS: BehaviorData.xml:5182, MapScript:62044:
   *   Period 0.5s, Duration 3s, dmg/tick = 0.06·maxLife/6 = 0.01·maxLife.
   *   Not biological-gated.
   */
  MasterChefsRollingPin: [
    {
      trigger: "on-hit",
      kind: "dot",
      params: {
        buffId: "RollingPinLifePact",
        maxLifeFrac: 0.01,     // KTS: 0.06·maxLife/6 = 0.01·maxLife per tick
      },
    },
  ],

  // -- Task 3: lifesteal + Celestial aura-heal rows --------------------------

  /**
   * WitchersEye — on-hit lifesteal (Task 3) + on-equip warmth drain-reducer (Task 4).
   * KTS: MapScript:57629 "GravesingerEye" — heal = 0.75 × finalDamage,
   *   only when the wielder is FeintReady AND the hit crits.
   * KTS: MapScript:4449 gf_WarmthDrainFactorGeneric — while WitchersEye behavior
   *   active, multiply night warmth-drain by 0.65.
   * CONCERN: FeintReady is a KTS buff; no Feint system exists in TribeZ v1.
   *   The gate is implemented against the crit flag only (result.isCrit).
   *   FeintReady will be wired when Feint ships — see combat-procs.ts comment.
   */
  WitchersEye: [
    {
      trigger: "on-hit",
      kind: "lifesteal",
      params: {
        healFrac: 0.75,        // KTS: 0.75 × finalDamage (MS:57629)
        requiresCrit: true,    // KTS: gated on crit + FeintReady
        requiresFeint: true,   // KTS: FeintReady gate — STUB (no Feint system in v1)
      },
    },
    {
      trigger: "on-equip",
      kind: "warmth-factor",
      params: {
        multiplier: 0.65,          // KTS: MS:4449 — 35% warmth drain reduction
        behaviorId: "WitchersEye", // equipment-derived behavior gate
      },
    },
  ],

  /**
   * GravesingersClaw — on-hit lifesteal, unconditional.
   * KTS: MapScript:56340 "GravesPromise" — heal = 0.10 × damage.
   * No gate (procs on any physical hit).
   */
  GravesingersClaw: [
    {
      trigger: "on-hit",
      kind: "lifesteal",
      params: {
        healFrac: 0.10,        // KTS: 0.10 × damage (MS:56340 GravesPromise)
      },
    },
  ],

  /**
   * CelestialOrb — on-autoattack hit: heal lowest-HP ally within 8u.
   * KTS: MS:57155, EffectData.xml:11211 "CelestialOrb" / "CelestialOrbHeal":
   *   Amount = (10 + spellPower) × 1.5; per-target 2s ICD ("CelestialOrbCooldown").
   *   Only the on-hit ally-heal is ported here — NOT the moon-orb projectile.
   */
  CelestialOrb: [
    {
      trigger: "on-hit",
      kind: "aura-heal",
      params: {
        healBase: 10,          // KTS: 10 + spellPower (EffectData.xml:11211)
        healSpellCoeff: 1.5,   // KTS: × 1.5 multiplier
        range: 8,              // KTS: 8u radius (EffectData.xml:11211)
        icdBuffId: "CelestialOrbCooldown",  // 2s per-target ICD
      },
    },
  ],

  /**
   * CelestialMoon — same aura-heal as CelestialOrb.
   * KTS: Same EffectData.xml:11211 "CelestialOrb" heal sub-effect.
   *   Moon-orb projectile variant out-of-scope (exotic one-off).
   */
  CelestialMoon: [
    {
      trigger: "on-hit",
      kind: "aura-heal",
      params: {
        healBase: 10,          // KTS: EffectData.xml:11211 — same formula
        healSpellCoeff: 1.5,
        range: 8,
        icdBuffId: "CelestialOrbCooldown",
      },
    },
  ],

  /**
   * CelestialDragon — same aura-heal as CelestialOrb.
   * KTS: Same EffectData.xml:11211 "CelestialOrb" heal sub-effect.
   */
  CelestialDragon: [
    {
      trigger: "on-hit",
      kind: "aura-heal",
      params: {
        healBase: 10,          // KTS: EffectData.xml:11211 — same formula
        healSpellCoeff: 1.5,
        range: 8,
        icdBuffId: "CelestialOrbCooldown",
      },
    },
  ],

  // -- Task 6: CC items (on-hit + thrown/active) -----------------------------

  /**
   * MurlocDoomhammer — on-hit stun with 10s ICD.
   * KTS: BehaviorData.xml "MurlocStun" Duration 1s, ICD "MurlocICD" Duration 10s.
   * Applies MurlocStun to the TARGET; stamps MurlocICD on the ATTACKER.
   * Re-hit within 10s is blocked by hasBuff(attacker, "MurlocICD").
   */
  MurlocDoomhammer: [
    {
      trigger: "on-hit",
      kind: "cc",
      params: {
        buffId: "MurlocStun",          // applied to target
        icdBuffId: "MurlocICD",        // stamped on attacker; blocks re-proc within 10s
        durationMs: 1000,              // KTS 1s stun
        icdMs: 10000,                  // KTS 10s ICD
      },
    },
  ],

  /**
   * OutlandishHeartpiercer — on-crit-hit stun vs biological targets, 7.5s ICD.
   * KTS: EffectData.xml "HPStun" Duration 1s, biological, crit-gated.
   *   ICD "HeartpiercerICD" Duration 7.5s on the attacker.
   * v1: all Cube targets are biological (targeting.ts convention), so biological
   * gate is always satisfied. crit gate: only fires when result.isCrit.
   */
  OutlandishHeartpiercer: [
    {
      trigger: "on-hit",
      kind: "cc",
      params: {
        buffId: "HeartpiercerStun",
        icdBuffId: "HeartpiercerICD",
        durationMs: 1000,              // KTS 1s
        icdMs: 7500,                   // KTS 7.5s ICD
        requiresCrit: true,            // KTS: crit-gated
        biological: true,              // KTS: biological-only (always true vs Cubes in v1)
      },
    },
  ],

  /**
   * DrunkenBooze — active throw: AoE daze (6s DrunkenBoozeDebuff) at target point, r=3u.
   * KTS: MapScript gf_DrunkenBooze — thrown bottle, AoE r=3, daze 6s,
   *   MS×0.1 floor 0.75. Modeled as DrunkenBoozeDebuff
   *   (stat-mod buff: speedMultBp−8000 for 6s).
   * NOTE: −35% dmg-dealt (attackDamageMod) not implemented in v1 (derived
   *   attackDamage can't be modded yet) — tracked in QUEUE.md follow-ups.
   * Server handler: throwDrunkenBooze in items/active-items.ts.
   * Client msg: "throw-drunken-booze" { x, z }.
   */
  DrunkenBooze: [
    {
      trigger: "active",
      kind: "cc",
      params: {
        buffId: "DrunkenBoozeDebuff",
        aoe: true,
        aoeRadius: 3,                  // KTS r=3u throw AoE
      },
    },
  ],

  /**
   * DiseasedCocktail — active throw: AoE slow (IndigestionSlow per stack), r=3u.
   * KTS: MapScript "IndigestionSlow" 0.5s/stack, −6.5%/stack MS, stacking up to 6.
   *   Extend: reapply slides the duration forward (applyBuff refresh-on-reapply
   *   handles this naturally). Max 6 stacks = −39% MS.
   * Server handler: throwDiseasedCocktail in items/active-items.ts.
   * Client msg: "throw-diseased-cocktail" { x, z }.
   */
  DiseasedCocktail: [
    {
      trigger: "active",
      kind: "cc",
      params: {
        buffId: "IndigestionSlow",
        // v1: stacking/maxStacks params are inert — single-stack refresh-only
        // (applyBuff refresh-on-reapply). KTS extends on re-hit, 6-stack cap.
        stacking: true,
        maxStacks: 6,
        aoe: true,
        aoeRadius: 3,
      },
    },
  ],

  /**
   * ReapersScythe — active on targeted enemy: silence for 2.5s.
   * KTS: BehaviorData.xml "ScytheSilence" Duration 2.5s, 21 abilities blocked.
   *   v1: all player abilities are blocked by the silence flag — no per-ability list.
   * Server handler: useReapersScythe in items/active-items.ts (per-caster CD).
   * Client msg: "use-reapers-scythe" { targetId }.
   */
  ReapersScythe: [
    {
      trigger: "active",
      kind: "cc",
      params: {
        buffId: "ReapersScytheSilence",
        durationMs: 2500,              // KTS 2.5s
        requiresTarget: true,          // needs a specific target, not AoE
      },
    },
  ],

  /**
   * DreadplateArmor — active AoE fear + auto-fear at < 30% HP.
   * KTS: MapScript "DreadplateFear" 1.3s, r=5 AoE, ICD 75s.
   *   Auto-trigger: fires the same effect when the wearer drops below 30% HP
   *   (if the ICD permits). v1: fear = canMove false + canAttack false.
   * Server handler: useDreadplateArmor + tickDreadplateAutoFear in items/active-items.ts.
   * Client msg: "use-dreadplate" (no payload).
   */
  DreadplateArmor: [
    {
      trigger: "active",
      kind: "cc",
      params: {
        buffId: "DreadplateFear",
        icdBuffId: "DreadplateICD",
        icdMs: 75000,                  // KTS 75s ICD
        aoe: true,
        aoeRadius: 5,                  // KTS r=5u AoE
        autoAtLowHp: true,             // KTS auto at < 30% HP
        lowHpThreshold: 0.3,           // 30% HP threshold
      },
    },
  ],

  /**
   * UnstableConcoction — active throw: AoE stun (1.15s) ONLY vs HeavyIronBreastplate wearers.
   * KTS: MapScript "BurningSensation" 1.15s stun, AoE r=3, biological,
   *   only targets wearing HeavyIronBreastplate in armor slot.
   * Server handler: throwUnstableConcoction in items/active-items.ts.
   * Client msg: "throw-unstable-concoction" { x, z }.
   */
  UnstableConcoction: [
    {
      trigger: "active",
      kind: "cc",
      params: {
        buffId: "BurningSensationStun",
        durationMs: 1150,              // KTS 1.15s
        requiresItemOnTarget: "HeavyIronBreastplate",  // only vs armored targets
        aoe: true,
        aoeRadius: 3,
      },
    },
  ],

  // -- Task 9: dashes (StagHuntersStiletto charge, ForestersMace leap) -------

  /**
   * StagHuntersStiletto — active charge to a targeted enemy.
   * KTS: WeaponData.xml:245 charge weapon + MapScript MS:64717 — per-tick
   *   UnitSetPosition charge toward the target, decelerating 0.75 → 0.25 u/tick
   *   (16fps), stops on PointPathingPassable failure (walls). On arrival deals
   *   weapon damage; if the charge STARTED in melee range (start distance ≤ 2.2u
   *   per KTS MS:64746 — `dist = DistanceBetweenPoints - 1.2; if dist <= 1`),
   *   also Disarms the target 1.2s. 5.5u cast range, 20s cooldown.
   * Server handler: startStilettoCharge in items/item-dash.ts (per-tick advance
   *   in the movement tick). Client msg: "use-stiletto" { targetId }.
   */
  StagHuntersStiletto: [
    {
      trigger: "active",
      kind: "dash",
      params: {
        cooldownBuffId: "StilettoCooldown",
        disarmBuffId: "StagDisarm",
        castRange: 5.5,          // KTS 5.5u cast range
        speedKtsStart: 0.75,     // KTS u/KTS-tick (16fps) at charge start
        speedKtsMin: 0.25,       // KTS u/KTS-tick floor after decel
        decelKts: 0.04,          // KTS MS:64728 per-tick deceleration (0.75 → 0.25)
        meleeRange: 2.2,         // KTS MS:64746 disarm gate: start distance ≤ 2.2u
      },
    },
  ],

  /**
   * ForestersMace — active leap to a nearby tree.
   * KTS: MapScript MS:66091 — leap toward the nearest tree (terrain-ignoring,
   *   0.34 u/tick @ 16fps), grants +25% damage reduction (ForestersLeap) for 4s
   *   on landing. Requires a tree in range (IsNearbyTree); no-op otherwise.
   *   27s cooldown.
   * Server handler: startForestersLeap in items/item-dash.ts. Client msg:
   *   "use-foresters-mace" (no payload — nearest tree resolved server-side).
   */
  ForestersMace: [
    {
      trigger: "active",
      kind: "leap",
      params: {
        cooldownBuffId: "ForestersMaceCooldown",
        drBuffId: "ForestersLeap",
        treeRange: 12,           // search radius for a nearby tree
        speedKts: 0.34,          // KTS u/KTS-tick (16fps), terrain-ignoring
        landOffset: 1.5,         // stop this far short of the tree center
      },
    },
  ],

  // -- Task 4: warmth drain-reducer consumables (on-consume / on-equip) -----

  /**
   * GiantSunfish — on-consume warmth drain-reducer (via cooking-buff buff).
   * KTS: BehaviorData.xml:9393 "GiantSunfish" Duration 360s — warmth drain
   *   factor 0.5 while "GiantSunfish" transient behavior is active.
   * Mechanism: cooking-buff handler's applyBehavior adds "GiantSunfish"
   *   transient for 360s → registerWarmthFactor entry fires. warmth-factors.ts.
   */
  GiantSunfish: [
    {
      trigger: "on-consume",
      kind: "warmth-factor",
      params: {
        multiplier: 0.5,            // KTS: BehaviorData.xml:9393 — 50% drain reduction
        behaviorId: "GiantSunfish", // transient added by cooking-buff applyBehavior
        durationMs: 360000,
      },
    },
  ],

  /**
   * BurgundianSmokedBearCutlet — on-consume warmth drain-reducer.
   * KTS: MapScript.galaxy:4474 — "reduces warmth loss by 15%" for 360s via
   *   "WellFedCutlet" behavior applied by cooking-buff handler.
   * Mechanism: cooking-buff handler's applyBehavior adds "WellFedCutlet"
   *   transient for 360s → registerWarmthFactor gated on "WellFedCutlet".
   */
  BurgundianSmokedBearCutlet: [
    {
      trigger: "on-consume",
      kind: "warmth-factor",
      params: {
        multiplier: 0.85,          // KTS: 15% reduction = ×0.85
        behaviorId: "WellFedCutlet",
        durationMs: 360000,
      },
    },
  ],

  // -- Task 10: spell-cast proc rows (on-cast, Family 5) --------------------

  /**
   * LiquidatorSpellblade — on-cast: arm LiquidatorSpellbladeActive (next attack
   * = blink-strike, then 5s disable after).
   * KTS: MS:18003 LiquidatorSpellblade gf_SpellCast behavior.
   * v1: arms the buff (1-charge marker); blink-strike sub-mechanic is a follow-up.
   */
  LiquidatorSpellblade: [
    {
      trigger: "on-cast",
      kind: "cast-proc",
      params: {
        buffId: "LiquidatorSpellbladeActive",
        armingCharges: 1, // 1 charge = next-attack rider (follow-up: implement blink)
      },
    },
  ],

  /**
   * RockbiterBattleAxe — on-cast: +1 RockAxeLifeRegen stack (life regen).
   * KTS: MS:18000 — adds a RockAxeLifeRegen behavior stack per cast, up to 6.
   * v1: re-apply increments chargesLeft (capped at 6); onTick heals chargesLeft HP/sec.
   */
  RockbiterBattleAxe: [
    {
      trigger: "on-cast",
      kind: "cast-proc",
      params: {
        buffId: "RockAxeLifeRegen",
        stackOnReapply: true, // re-apply: increment chargesLeft up to maxStacks
        maxStacks: 6,          // KTS cap
      },
    },
  ],

  /**
   * ForgottenShredder — on-cast: +2 Dismantle weapon charges.
   * KTS: MS:18013 ForgottenShredder gf_SpellCast adds 2 Dismantle charges.
   * v1: applies DismantleWeaponCharge marker (Dismantle charge system is a follow-up).
   * FOLLOW-UP: when Dismantle weapon-charge system ships, replace marker with charge-add.
   */
  ForgottenShredder: [
    {
      trigger: "on-cast",
      kind: "cast-proc",
      params: {
        buffId: "DismantleWeaponCharge",
        chargesAdded: 2, // KTS +2 charges; actual charge system is a follow-up
      },
    },
  ],

  /**
   * ScrollPadsoftheDarkMagi — on-cast: heal caster 75% of the spell's mana cost.
   * KTS: MS:18142 — heal = 0.75 × cost; only when cost > 0.
   * Fully implemented: direct hp heal, no buff needed.
   */
  ScrollPadsoftheDarkMagi: [
    {
      trigger: "on-cast",
      kind: "cast-proc",
      params: {
        healPctOfManaCost: 0.75, // KTS: 75% of mana cost (MS:18142)
        requiresManaCost: true,  // only fires when cost > 0
      },
    },
  ],

  /**
   * IronStuddedVest — on-cast: apply MysticShield self-shield.
   * KTS: IronStuddedVest cast-driven MysticShield behavior.
   * shieldAmount: 30 base (KTS-faithful for the self-shield).
   */
  IronStuddedVest: [
    {
      trigger: "on-cast",
      kind: "cast-proc",
      params: {
        buffId: "MysticShieldBuff",
        shieldAmount: 30, // KTS: MysticShield base shield
      },
    },
  ],

};

// ---------------------------------------------------------------------------
// Accessor — returns [] for items not in the table (safe default everywhere).
// ---------------------------------------------------------------------------

/** Returns the structured effects for `itemId`, or [] if none are registered.
 *  The canonical access path — never read ITEM_EFFECTS directly in game code. */
export function getItemEffects(itemId: string): ItemEffect[] {
  return (ITEM_EFFECTS[itemId] as ItemEffect[] | undefined) ?? [];
}

// ---------------------------------------------------------------------------
// KNOWN_NOOP_ITEMS — items deliberately NOT in ITEM_EFFECTS
//
// These items were originally tagged in `pendingEffects` but are either
// mistagged (the KTS mechanism doesn't match the tag) or are exotic one-offs
// we deliberately deferred. The qa-item-effects.ts test asserts every item
// in this list has NO ITEM_EFFECTS row — so a future edit can't accidentally
// half-wire them.
//
// NOTE: mana-burn items (EviscerateTrigger, SanguineWisp, DarkwhisperShawl,
// MoonHuntersCape) are NOT listed here because they do NOT exist in the ITEMS
// registry (items-data.json). The actual mana-burn tag carriers in the
// registry are CravensFishSticker (self-cost, listed below) and others where
// mana-burn is incidental. The mistagging note is preserved as a comment.
//
// Format: { id: ItemId, reason: string }
// ---------------------------------------------------------------------------
export interface KnownNoopEntry {
  readonly id: string;
  readonly reason: string;
}

export const KNOWN_NOOP_ITEMS: ReadonlyArray<KnownNoopEntry> = [
  // Exotic one-offs — deferred to follow-up
  { id: "BoneshardBlade",         reason: "exotic-item-morph" },
  { id: "FrostNeedle",            reason: "exotic-item-morph" },
  { id: "FrostBarrier",           reason: "exotic-item-morph" },
  { id: "ScatterShotgun",         reason: "knockback" },
  { id: "Bloodcleaver",           reason: "heal-on-kill" },
  { id: "MysteriousSpoon",        reason: "chain-lightning" },
  { id: "CravensFishSticker",     reason: "self-cost" },
  { id: "ZephyrsGuard",           reason: "projectile-exotic" },
];
